import React, { useEffect, useMemo, useState } from "react";
import _, { set } from "lodash";
import cx from "classnames";

import { useDispatch } from "react-redux";
import { SimulationContext } from "../../../context";
import { Objectives, Simulation, SimulationValidationError } from "src/types/coaching/simulation";
import { generateSimulationSummary, invalidateGenerateSimulationSummaryState, updateSimulationProperty } from "src/redux/actions/coaching/simulation";

import styles from "./objectives.module.scss";
import { ChatContainer } from "../../chat";
import TextareaAutosize from "react-autosize-textarea";
import Button from "src/components/button";
import ScrollToBottom from 'react-scroll-to-bottom';
import { ErrorMessage } from "../../../../components/error";
import Tooltip from "src/components/tooltip";
import { prepareObjectivesRequest, validateSimulationRequest } from "../../../helper";
import { useSelector } from "react-redux";

interface ObjectivesEditorProps {
  simulation: Simulation;
}

export const ObjectivesEditor: React.FC<ObjectivesEditorProps> = ({ simulation }) => {
  const dispatch = useDispatch();
  const simulationContext = React.useContext(SimulationContext);

  const persistedObjectives = simulation?.objectives;
  const [objectives, setObjectives] = useState<Objectives | undefined>(persistedObjectives);
  const [errors, setErrors] = useState<SimulationValidationError[]>([]);

  const description = objectives?.description || "";
  const goals = objectives?.goals || "";
  const painPoints = objectives?.pain_points || "";
  const assessmentQuestions = objectives?.assessment_questions || "";
  const acceptableResponses = objectives?.acceptable_responses || "";
  const dealKillers = objectives?.deal_killers || "";
  const opportunityTriggers = objectives?.opportunity_triggers || "";

  const title = useMemo(() => _.get(simulation, 'description'), [simulation])

  // error messages
  const validationErrors = useSelector((state: any) => {
    return _.get(state, `coaching.simulations.edit.errors`) as SimulationValidationError[];
  }, _.isEqual);

  const { generatingSummary, generatedSummary } = useSelector((state: any) => {
    return {
      generatingSummary: _.get(state, `coaching.simulations.edit.generateSummary.generating`, false),
      generatedSummary: _.get(state, `coaching.simulations.edit.generateSummary.generated`, false),
    }
  }, _.isEqual);

  const descriptionError = useMemo(() => {
    return _.find(errors, { type: "objectives", property: "description" }) || null;
  }, [errors]);

  const goalsError = useMemo(() => {
    return _.find(errors, { type: "objectives", property: "goals" }) || null;
  }, [errors]);

  const painPointsError = useMemo(() => {
    return _.find(errors, { type: "objectives", property: "pain_points" }) || null;
  }, [errors]);

  const assessmentQuestionsError = useMemo(() => {
    return _.find(errors, { type: "objectives", property: "assessment_questions" }) || null;
  }, [errors]);

  const acceptableResponsesError = useMemo(() => {
    return _.find(errors, { type: "objectives", property: "acceptable_responses" }) || null;
  }, [errors]);

  const dealKillersError = useMemo(() => {
    return _.find(errors, { type: "objectives", property: "deal_killers" }) || null;
  }, [errors]);

  const opportunityTriggersError = useMemo(() => {
    return _.find(errors, { type: "objectives", property: "opportunity_triggers" }) || null;
  }, [errors]);

  useEffect(() => {
    setObjectives(persistedObjectives);
  }, [persistedObjectives]);

  useEffect(() => {
    setErrors(validationErrors);
  }, [validationErrors]);

  useEffect(() => {
    if (generatedSummary) {
      dispatch(invalidateGenerateSimulationSummaryState(simulation.id!));

      if (simulationContext.editObjectives) {
        simulationContext.editObjectives(false);
      }
    }
  }, [generatedSummary]);

  const handlePropertyChange = (property: string, value: any) => {
    setObjectives((prevObjectives) => {
      const updatedObjectives = {
        ...prevObjectives || {},
        [property]: value
      } as Objectives;
      return updatedObjectives;
    });
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("description", event.target.value);
  }

  const handleGoalsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("goals", event.target.value);
  }

  const handlePainPointsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("pain_points", event.target.value);
  }

  const handleAssessmentQuestionsChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("assessment_questions", event.target.value);
  }

  const handleAcceptableResponsesChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("acceptable_responses", event.target.value);
  }

  const handleDealKillersChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("deal_killers", event.target.value);
  }

  const handleOpportunityTriggersChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    handlePropertyChange("opportunity_triggers", event.target.value);
  }

  const handleConfirm = () => {
    const changes = prepareObjectivesRequest({
      description: "",
      goals: "",
      pain_points: "",
      assessment_questions: "",
      acceptable_responses: "",
      deal_killers: "",
      opportunity_triggers: ""
    }, objectives);

    dispatch(updateSimulationProperty({ simulation: simulation.id!, property: "objectives", value: objectives }));

    dispatch(generateSimulationSummary({
      simulation: simulation.id!,
      description: simulation.description || "",
      objectives: changes
    }));
  }

  const handleCancel = () => {
    if (simulationContext.editObjectives) {
      simulationContext.editObjectives(false);
    }
  }

  const handleGetParameters = () => {
    const parameters = {
      description: description,
      goals: goals,
      pain_points: painPoints,
      assessment_questions: assessmentQuestions,
      acceptable_responses: acceptableResponses,
      deal_killers: dealKillers,
      opportunity_triggers: opportunityTriggers
    };

    return parameters;
  }

  const handleParametersChange = (parameters: any) => {
    _.keys(parameters).forEach((key) => {
      const value = parameters[key];
      if (value) {
        handlePropertyChange(key, value);
      }
    });
  }

  useEffect(() => {
    if (objectives) {
      const simulationRequest = {
        ...simulation,
        objectives: objectives
      }

      const validationErrors = validateSimulationRequest(simulationRequest);
      setErrors(validationErrors);
    }
  }, [objectives]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.title} onClick={handleCancel}>
          <button className={styles.backButton}>
            <i className={cx(styles.backIcon, 'paperflite-left-arrow')} />
          </button>
          <h2>Scenario</h2>
        </div>

        <div className={styles.actions}>
          <Button variant="secondary" className={cx(styles.action, styles.confirm)}
            icon="paperflite-tick" text={generatingSummary ? "Confirming..." : "Confirm"}
            disabled={generatingSummary}
            onClick={handleConfirm} />
          <Button className={cx(styles.action, styles.cancel)}
            icon="paperflite-cancel"
            text="Cancel"
            disabled={generatingSummary}
            onClick={handleCancel}
          />
        </div>
      </div>
      <p className={styles.description}>Edit your scenario</p>

      <div className={styles.body}>
        <ScrollToBottom className={cx(styles.left, styles.chat)} scrollViewClassName={styles.scrollView} followButtonClassName={styles.scrollButton}>
          <div className={styles.header}>
            <h4>Generate objectives with seek</h4>
          </div>
          <div className={styles.chats}>
            <ChatContainer simulation={simulation}
              configurationType="objectives"
              getParameters={handleGetParameters}
              onChange={handleParametersChange}
            />
          </div>
        </ScrollToBottom>
        <div className={cx(styles.right, styles.organization)}>
          <div className={styles.summaryView}>
            <TextareaAutosize
              className={styles.summary}
              rows={6}
              value={description}
              placeholder={'Type here the description...'}
              spellCheck={true}
              onChange={handleDescriptionChange}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />

            <ErrorMessage error={descriptionError} />
          </div>
          <div className={styles.formView}>
            <div className={cx(styles.field)}>
              <FieldLabel label="Prospect Objectives" error={goalsError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={goals}
                placeholder={'Type here the goals...'}
                spellCheck={true}
                onChange={handleGoalsChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Company Pain Points" error={painPointsError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={painPoints}
                placeholder={'Type here the interaction...'}
                spellCheck={true}
                onChange={handlePainPointsChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Assessment Questions" error={assessmentQuestionsError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={assessmentQuestions}
                placeholder={'Type here the interaction...'}
                spellCheck={true}
                onChange={handleAssessmentQuestionsChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Acceptable Responses" error={acceptableResponsesError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={acceptableResponses}
                placeholder={'Type here the interaction...'}
                spellCheck={true}
                onChange={handleAcceptableResponsesChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Deal Killers" error={dealKillersError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={dealKillers}
                placeholder={'Type here the interaction...'}
                spellCheck={true}
                onChange={handleDealKillersChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
            <div className={styles.field}>
              <FieldLabel label="Opportunity Triggers" error={opportunityTriggersError} />
              <TextareaAutosize
                className={styles.input}
                rows={1}
                value={opportunityTriggers}
                placeholder={'Type here the interaction...'}
                spellCheck={true}
                onChange={handleOpportunityTriggersChange}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface FieldLabelProps {
  label: string;
  error?: SimulationValidationError | null;
}

const FieldLabel: React.FC<FieldLabelProps> = ({ label, error }) => {
  return (
    <Tooltip placement="top-start" content={error?.message} disabled={!error}>
      <label className={cx({ [styles.error]: error })}>
        {error && <i className={cx("fa-solid", "fa-triangle-exclamation")}></i>}
        {label}
      </label>
    </Tooltip>
  );
}